import { Tile, SectionHeader, Pill } from "../components/primitives";
import { useTheme } from "../components/ThemeProvider";
import { contrastRatio } from "../lib/theme";

// Admin / Settings. Surfaces the theme preference, the tenant branding overrides, and the live
// palette so an operator can see exactly what the tenant's build renders. Read-mostly here;
// tenant provisioning + branding edits go through the API's admin routes.
const SWATCHES: { name: string; varName: string }[] = [
  { name: "Honey gold", varName: "--gold" },
  { name: "Gold hover", varName: "--gold-hover" },
  { name: "Comb", varName: "--comb" },
  { name: "Hive brown", varName: "--brown" },
  { name: "Brown light", varName: "--brown-light" },
  { name: "Wax", varName: "--wax" },
  { name: "Nectar", varName: "--teal" },
  { name: "Canvas", varName: "--bg-0" },
];

export function AdminView() {
  const { theme, preference, setPreference, branding } = useTheme();

  // Live contrast readout for body text on the canvas — proves the AA floor holds in-app.
  const style = getComputedStyle(document.documentElement);
  const text1 = style.getPropertyValue("--text-1").trim();
  const bg0 = style.getPropertyValue("--bg-0").trim();
  const bodyContrast = text1 && bg0 && text1.startsWith("#") && bg0.startsWith("#") ? contrastRatio(text1, bg0) : null;

  return (
    <>
      <Tile>
        <SectionHeader title="Appearance">
          <Pill className="pill-gold">Hive</Pill>
        </SectionHeader>
        <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: "var(--text-3)", fontWeight: 600 }}>Theme (saved to your profile):</span>
          {(["system", "light", "dark"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPreference(p)}
              className={`nav ${preference === p ? "on" : ""}`}
              style={{ padding: "6px 13px", fontSize: 12, textTransform: "capitalize" }}
              aria-pressed={preference === p}
            >
              {p}
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-3)" }}>
            Resolving to <strong style={{ color: "var(--text-1)" }}>{theme}</strong>
          </span>
        </div>
        {bodyContrast !== null && (
          <div style={{ marginTop: 12, fontSize: 12, color: bodyContrast >= 4.5 ? "var(--teal)" : "var(--red)" }}>
            Body-text contrast on canvas: <strong>{bodyContrast.toFixed(2)}:1</strong>{" "}
            {bodyContrast >= 4.5 ? "✓ passes WCAG AA (4.5:1)" : "✗ below AA"}
          </div>
        )}
      </Tile>

      <Tile>
        <SectionHeader title="Palette" />
        <div style={{ height: 14 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 9 }}>
          {SWATCHES.map((s) => (
            <div key={s.varName} style={{ borderRadius: 11, overflow: "hidden", border: "1px solid var(--border)" }}>
              <div style={{ height: 52, background: `var(${s.varName})`, borderTop: s.varName === "--bg-0" ? "1px solid var(--border)" : undefined }} />
              <div style={{ fontSize: 10, fontWeight: 700, padding: "7px 8px 2px" }}>{s.name}</div>
              <div style={{ fontSize: 9.5, color: "var(--text-3)", padding: "0 8px 8px", fontFamily: "ui-monospace, monospace" }}>
                {getComputedStyle(document.documentElement).getPropertyValue(s.varName).trim()}
              </div>
            </div>
          ))}
        </div>
      </Tile>

      <Tile>
        <SectionHeader title="Tenant branding" />
        <div style={{ height: 10 }} />
        <p style={{ fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.6 }}>
          Per-tenant branding overrides <code>--gold</code>, <code>--gradient-gold</code> and the logo only. Hive brown,
          the surface warmth, the honeycomb texture and the glass system are Action Center's identity and are never
          overridden. In light mode a brand gold that fails 4.5:1 for text is used for fills only; small text falls back
          to the accessible token gold.
        </p>
        <dl style={{ marginTop: 12, fontSize: 12.5, display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px" }}>
          <dt style={{ color: "var(--text-3)" }}>Display name</dt>
          <dd>{branding?.display_name ?? "— (product default)"}</dd>
          <dt style={{ color: "var(--text-3)" }}>Brand gold</dt>
          <dd style={{ fontFamily: "ui-monospace, monospace" }}>{branding?.gold ?? "— (default #E8A317 / light #B8860B)"}</dd>
          <dt style={{ color: "var(--text-3)" }}>Logo</dt>
          <dd>{branding?.logo_url ? "custom image" : `glyph ${branding?.logo_glyph ?? "🐝"}`}</dd>
        </dl>
      </Tile>
    </>
  );
}
